import type { UserInfo_UserFragment } from '@graphql/__generated__/graphql';

type BasicContact = {
  first_name?: string | null;
  last_name?: string | null;
};

export const getDisplayNameFromNameParts = (
  firstName?: string | null,
  lastName?: string | null,
  fallback = '',
) => [firstName, lastName].filter(Boolean).join(' ').trim() || fallback;

export type ContactLike = {
  contact?: BasicContact | null;
  anonymized_contact?: string | null;
};

export type ContactNameParts = {
  firstName: string;
  rawLastName: string;
};

export const parseAnonymizedContact = (
  anonymizedContact?: string | null,
): ContactNameParts => {
  if (!anonymizedContact) {
    return { firstName: '', rawLastName: '' };
  }

  const parts = anonymizedContact.split(' ').filter(Boolean);
  let rawLastName = '';

  if (anonymizedContact.endsWith('.')) {
    rawLastName = parts.pop() ?? '';
  }

  return {
    firstName: parts.join(' ').trim(),
    rawLastName: rawLastName.trim(),
  };
};

export const createAnonymizedContactUser = (
  anonymizedContact?: string | null,
): UserInfo_UserFragment => {
  const { firstName, rawLastName } = parseAnonymizedContact(anonymizedContact);

  return {
    id: anonymizedContact ?? '',
    first_name: firstName,
    last_name: rawLastName,
    full_name: [firstName, rawLastName].filter(Boolean).join(' '),
    user_images: [],
    phone_numbers: [],
    emails: [],
  };
};

export const getContactDisplayNames = (
  target: ContactLike | null | undefined,
  canViewFullLastName: boolean,
) => {
  if (!target) {
    return {
      firstName: '',
      rawLastName: '',
      displayLastName: '',
      displayName: '',
    };
  }

  const anonymized = parseAnonymizedContact(target.anonymized_contact);

  const firstName = (
    target.contact?.first_name ??
    anonymized.firstName ??
    ''
  ).trim();
  const rawLastName = (
    target.contact?.last_name ??
    anonymized.rawLastName ??
    ''
  ).trim();

  const displayLastName = canViewFullLastName
    ? rawLastName
    : rawLastName !== ''
    ? `${rawLastName.slice(0, 1)}.`
    : '';

  const displayName = `${firstName} ${displayLastName}`.trim();

  return {
    firstName,
    rawLastName,
    displayLastName,
    displayName,
  };
};

export const getInitialsFromDisplayName = (name?: string | null) => {
  if (name == null || name.trim() === '') {
    return '?';
  }

  const parts = name.trim().split(' ').filter(Boolean);

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
